import React, { useMemo, useState } from "react";
import { Check, Clapperboard, Copy, Filter, Search, Sparkles } from "lucide-react";
import {
  AI_ACTING_CARDS,
  AI_ACTING_CURVES,
  AI_ACTING_FILTERS,
  AI_ACTING_LEVELS,
  AI_ACTING_PROMPT_TEMPLATES,
} from "./aiActingGuideData";
import "./AIActingGuidePage.css";

const timelineLabels = ["0-1秒", "1-2秒", "2-4秒"];

const filterCards = (cards, filter, query) => {
  const keyword = query.trim().toLowerCase();
  return cards.filter((card) => {
    const filterMatched = filter === "all"
      || card.type === filter
      || (filter === "restrained" && card.tags.includes("restrained"))
      || (filter === "peak" && card.tags.includes("peak"));
    if (!filterMatched) return false;
    if (!keyword) return true;
    return [
      card.emotion,
      card.name,
      card.core,
      card.eyes,
      card.mouth,
      card.body,
      card.breath,
      card.shot,
      card.prompt,
      card.avoid,
    ].some((value) => String(value).toLowerCase().includes(keyword));
  });
};

const FacePreview = ({ type, level }) => (
  <div className={`acting-face acting-face--${type} acting-face--level-${level}`} aria-hidden="true">
    <span className="acting-face__brow acting-face__brow--left" />
    <span className="acting-face__brow acting-face__brow--right" />
    <span className="acting-face__eye acting-face__eye--left" />
    <span className="acting-face__eye acting-face__eye--right" />
    <span className="acting-face__mouth" />
    <span className="acting-face__tear" />
  </div>
);

const LevelMeter = ({ level }) => (
  <div className="acting-card__meter" aria-label={`情绪等级 ${level}`}>
    {Array.from({ length: 7 }, (_, index) => (
      <span key={index} className={index <= level ? "is-on" : ""} />
    ))}
  </div>
);

const PromptBlock = ({ prompt, copied, onCopy }) => (
  <div className="acting-prompt">
    <div className="acting-prompt__head">
      <span>可复制 AI 提示词</span>
      <button type="button" className="acting-copy-button" onClick={onCopy} title="复制提示词">
        {copied ? <Check size={14} /> : <Copy size={14} />}
        <span>{copied ? "已复制" : "复制"}</span>
      </button>
    </div>
    <pre>{prompt}</pre>
  </div>
);

const ActingCard = ({ card, copiedKey, onCopy }) => (
  <article className={`acting-card acting-card--${card.type} acting-card--level-${card.level}`}>
    <header className="acting-card__head">
      <div className="acting-card__tag">{card.emotion}</div>
      <div className="acting-card__title">
        <h3>{card.level}级 {card.name}</h3>
        <p>{card.core}</p>
      </div>
      <LevelMeter level={card.level} />
    </header>

    <div className="acting-card__body">
      <aside className="acting-card__visual">
        <FacePreview type={card.type} level={card.level} />
        <div className="acting-face-notes">
          <p><b>眼神</b>{card.eyes}</p>
          <p><b>嘴/下颌</b>{card.mouth}</p>
          <p><b>呼吸</b>{card.breath}</p>
        </div>
      </aside>

      <div className="acting-card__content">
        <div className="acting-info-grid">
          <div className="acting-info">
            <b>身体动作</b>
            <span>{card.body}</span>
          </div>
          <div className="acting-info">
            <b>镜头建议</b>
            <span>{card.shot}</span>
          </div>
        </div>

        <div className="acting-timeline">
          <b>3段时间轴</b>
          <div>
            {card.timeline.map((step, index) => (
              <section key={timelineLabels[index]} className="acting-timeline__step">
                <strong>{timelineLabels[index]}</strong>
                <span>{step}</span>
              </section>
            ))}
          </div>
        </div>

        <PromptBlock
          prompt={card.prompt}
          copied={copiedKey === `${card.type}-${card.level}`}
          onCopy={() => onCopy(`${card.type}-${card.level}`, card.prompt)}
        />

        <div className="acting-avoid">
          <b>反向限制：</b>{card.avoid}
        </div>
      </div>
    </div>
  </article>
);

const AIActingGuidePage = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [copiedKey, setCopiedKey] = useState("");

  const visibleCards = useMemo(
    () => filterCards(AI_ACTING_CARDS, activeFilter, query),
    [activeFilter, query],
  );

  const handleCopy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(""), 1200);
    } catch {
      setCopiedKey("");
    }
  };

  return (
    <div className="acting-guide-page">
      <section className="acting-hero">
        <div className="acting-hero__main">
          <div className="acting-kicker">
            <Clapperboard size={16} />
            <span>AI 角色表演参考</span>
          </div>
          <h1>AI人表演可视化导演表</h1>
          <p>
            面向生图、生视频和分镜提示词，把情绪拆成可见动作：眼神方向、眉眼变化、嘴角幅度、呼吸节奏、身体重心、镜头距离和时间轴。
          </p>
          <div className="acting-formula">
            <b>提示词公式</b>
            <span>角色身份 + 具体事件 + 情绪等级 + 面部动作 + 身体动作 + 呼吸/声音 + 时间轴 + 镜头 + 反向限制</span>
          </div>
        </div>

        <div className="acting-hero__side">
          <h2>写给 AI 的表演规则</h2>
          <ul>
            <li>少写抽象情绪，多写可见变化。</li>
            <li>每个变化都给触发点，避免随机表情。</li>
            <li>近景写微表情，中景写身体方向，全景写空间关系。</li>
            <li>强情绪必须写回落：喘息、沉默、虚脱或麻木。</li>
            <li>反向词要明确：避免夸张、卡通、假笑、乱吼、无理由哭。</li>
          </ul>
        </div>
      </section>

      <section className="acting-controls">
        <div className="acting-filter-row" aria-label="情绪筛选">
          <Filter size={15} />
          {AI_ACTING_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={filter.id === activeFilter ? "is-active" : ""}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <label className="acting-search">
          <Search size={15} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索：强忍、冷怒、特写、哭、笑..."
          />
        </label>
      </section>

      <section className="acting-section">
        <div className="acting-section__head">
          <h2>0-6级直观标尺</h2>
          <span>{AI_ACTING_CARDS.length} 张等级卡</span>
        </div>
        <div className="acting-level-grid">
          {AI_ACTING_LEVELS.map((item) => (
            <div key={item.level} className="acting-level">
              <b>{item.level} {item.label}</b>
              <span>{item.detail}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="acting-section">
        <div className="acting-section__head">
          <h2>提示词模板</h2>
          <Sparkles size={17} />
        </div>
        <div className="acting-template-grid">
          {AI_ACTING_PROMPT_TEMPLATES.map((template) => (
            <div key={template.title} className="acting-template">
              <h3>{template.title}</h3>
              <p>{template.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section key={activeFilter} className="acting-card-grid animate-in fade-in duration-200" aria-label="情绪等级卡片">
        {visibleCards.map((card) => (
          <ActingCard
            key={`${card.type}-${card.level}`}
            card={card}
            copiedKey={copiedKey}
            onCopy={handleCopy}
          />
        ))}
      </section>

      <section className="acting-section">
        <div className="acting-section__head">
          <h2>常用情绪曲线</h2>
          <span>可直接写进分镜备注</span>
        </div>
        <div className="acting-curve-grid">
          {AI_ACTING_CURVES.map((curve) => (
            <div key={curve.title} className="acting-curve">
              <h3>{curve.title}</h3>
              <div className="acting-curve__path">
                {curve.path.map((step, index) => (
                  <React.Fragment key={step}>
                    <span>{step}</span>
                    {index < curve.path.length - 1 && <i>到</i>}
                  </React.Fragment>
                ))}
              </div>
              <p>{curve.detail}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AIActingGuidePage;
